import { useCallback } from "react";
import { Control, useFieldArray } from "react-hook-form";
import type { Tag } from "@/shared/api/newsquawk-headlines";
import type { HeadlineForm } from "../models/HeadlineForm";
import type { Section } from "../models/Section";

/**
 * Custom hook for managing tags within react-hook-form using useFieldArray.
 * Consolidates all tag operations (recommended, additional, section-based) into one place.
 */
export function useTagField(control: Control<HeadlineForm>) {
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "tags",
  });

  /**
   * Toggle a tag selection - adds if not present, removes if already selected
   */
  const toggleTag = useCallback(
    (tag: Tag) => {
      const index = fields.findIndex((f) => f.tag_id === tag.tag_id);
      if (index >= 0) {
        remove(index);
      } else {
        append(tag);
      }
    },
    [fields, append, remove]
  );

  /**
   * Add a tag (if not already present)
   */
  const addTag = useCallback(
    (tag: Tag) => {
      if (fields.some((f) => f.tag_id === tag.tag_id)) return;
      append(tag);
    },
    [fields, append]
  );

  /**
   * Remove a tag by tag_id
   */
  const removeTag = useCallback(
    (tagId: number) => {
      const index = fields.findIndex((f) => f.tag_id === tagId);
      if (index >= 0) {
        remove(index);
      }
    },
    [fields, remove]
  );

  /**
   * Add section as a tag with a default score of 0.95
   * (sections are also tags in the new requirements)
   */
  const addSectionAsTag = useCallback(
    (section: Section) => {
      if (!section.tag_id) return;
      if (fields.some((f) => f.tag_id === section.tag_id)) return;

      append({
        tag_id: section.tag_id,
        name: section.name,
        score: 0.95,
      });
    },
    [fields, append]
  );

  /**
   * Check if a tag is currently selected
   */
  const hasTag = useCallback(
    (tagId: number) => fields.some((f) => f.tag_id === tagId),
    [fields]
  );

  /**
   * Select all tags from a list (used for "Select All" functionality)
   */
  const selectAll = useCallback(
    (tags: Tag[]) => {
      // Add only tags that aren't already selected
      const newTags = tags.filter(
        (tag) => !fields.some((f) => f.tag_id === tag.tag_id)
      );
      newTags.forEach((tag) => append(tag));
    },
    [fields, append]
  );

  // Convert field array entries back to Tag type (strip the id)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const tagsWithoutId: Tag[] = fields.map(({ id, ...tag }) => tag);

  return {
    tags: tagsWithoutId,
    fields, // Also expose the raw fields for key prop usage
    toggleTag,
    addTag,
    removeTag,
    addSectionAsTag,
    hasTag,
    selectAll,
    replaceTags: replace,
  };
}
